import { PencilLine } from 'lucide-react';
import { useId, useState, type ReactElement } from 'react';
import { Link } from 'react-router';

import { usePermission } from '@/hooks/usePermission';
import { formatCustomValue } from '@/components/documents/custom-field-format';
import type { DocumentStatus } from '@/lib/document-status';

import type { CustomFieldItem } from '../presentation';
import { CustomFieldsModal } from './CustomFieldsModal';

// Datos adicionales de la ficha (QA F1 #14): los campos propios que define la empresa
// (ubicación, protección, departamentos…) con su valor. Calidad los completa; un
// documento anulado ya no admite cambios. Sin campos definidos no hay tarjeta vacía:
// quien administra catálogos tiene el camino para definirlos (§E.2).

export function CustomFieldsCard({
  entryId,
  code,
  status,
  fields,
}: {
  entryId: string;
  code: string;
  status: DocumentStatus;
  fields: CustomFieldItem[];
}): ReactElement | null {
  const titleId = useId();
  const canFill = usePermission('quality:document:metadata');
  const canDefine = usePermission('quality:catalogs:manage');
  const [editing, setEditing] = useState(false);
  const canEdit = canFill && status !== 'anulado' && fields.length > 0;

  if (fields.length === 0 && !canDefine) {
    return null;
  }

  return (
    <section className="card rise d3" aria-labelledby={titleId}>
      <div className="ch">
        <h3 id={titleId}>Datos adicionales</h3>
        {canEdit && (
          <button type="button" className="btn mut sm" onClick={() => setEditing(true)}>
            <PencilLine size={14} aria-hidden="true" />
            Editar datos
          </button>
        )}
      </div>
      <div className="cb">
        {fields.length === 0 ? (
          <p className="soft" style={{ fontSize: 13, margin: 0 }}>
            La empresa todavía no definió campos propios para sus documentos.{' '}
            <Link to="/configuracion/catalogos?catalogo=campos">Definir campos propios</Link>
          </p>
        ) : (
          <dl className="kvs">
            {fields.map((f) => {
              const text = formatCustomValue(f.dataType, f.value);
              const empty = text === '—';
              return (
                <div key={f.key} className="kv">
                  <dt className="kk">{f.label}</dt>
                  <dd className={empty ? 'vv soft' : 'vv'}>
                    {empty ? (f.required ? 'Sin completar (obligatorio)' : 'Sin completar') : text}
                  </dd>
                </div>
              );
            })}
          </dl>
        )}
      </div>
      <CustomFieldsModal
        open={editing}
        entryId={entryId}
        code={code}
        fields={fields}
        onClose={() => setEditing(false)}
      />
    </section>
  );
}
